import React from "react";
import { Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import ActionButton from "@quest_editor/components/Buttons/ActionButton";
import BackButton from "@quest_editor/components/Buttons/BackButton";
import Breadcrumb from "@quest_editor/components/Breadcrumb";
import PromotionTypeChip from "@quest_editor/components/Chips/PromotionTypeChip";
import PromotionStatusChip from "@quest_editor/components/Chips/PromotionStatusChip";
import { useQuest, usePromotion, useUpdateQuest } from "@quest_editor/mocks";
import { LinkParamValue } from "@quest_editor/constants";
import { getLinkParamOptions } from "@quest_editor/utils/view/constLabels";
import ExternalLinkSection from "./ExternalLinkSection";

const QuestEditLink = () => {
  const { t } = useTranslation("questEditor");
  const navigate = useNavigate();
  const { id: questId } = useParams<{ id: string }>();

  const [linkTitle, setLinkTitle] = React.useState("");
  const [linkUrl, setLinkUrl] = React.useState("");
  const [linkParams, setLinkParams] = React.useState<LinkParamValue[]>([]);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const { data: questData } = useQuest(questId);
  const quest = questData?.quest;

  const { data: promotionData } = usePromotion(quest?.promotionId);
  const promotion = promotionData?.promotion;

  const availableLinkParams = getLinkParamOptions();
  const { updateQuest } = useUpdateQuest();
  const loadedQuestIdRef = React.useRef<string | undefined>();

  React.useEffect(() => {
    if (!quest || !questId) return;
    if (loadedQuestIdRef.current === questId) return;
    loadedQuestIdRef.current = questId;
    setLinkTitle(quest.linkTitle ?? "");
    setLinkUrl(quest.linkUrl ?? "");
    setLinkParams(quest.linkParams ?? []);
  }, [questId, quest]);

  const save = async () => {
    if (!questId) return;
    await updateQuest({ id: questId });
  };

  const handleSaveAndBack = async () => {
    if (!promotion || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await save();
      navigate(`/quest_editor/promotion/${promotion.id}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveAndNext = async () => {
    if (!quest || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await save();
      navigate(`/quest_editor/quest/${quest.id}/edit/purchase`);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!quest || !promotion) {
    return <Typography>{t("common.label.not_found")}</Typography>;
  }

  return (
    <Stack direction="column" spacing={3} sx={{ flex: 1, minHeight: 0 }}>
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
      >
        <Stack direction="column" spacing={2} alignItems="flex-start">
          <Breadcrumb items={[{ label: promotion.name, to: `/quest_editor/promotion/${promotion.id}` }, { label: quest.name }]} />
          <Stack direction="row" spacing={1}>
            <PromotionTypeChip type={promotion.type} />
            <PromotionStatusChip status={promotion.status} />
          </Stack>
        </Stack>
      </Stack>

      <ExternalLinkSection
        linkTitle={linkTitle}
        setLinkTitle={setLinkTitle}
        linkUrl={linkUrl}
        setLinkUrl={setLinkUrl}
        linkParams={linkParams}
        setLinkParams={setLinkParams}
        availableLinkParams={availableLinkParams}
      />

      <Stack
        direction="row"
        justifyContent="center"
        alignItems="center"
        pt={2}
        sx={{ position: "relative" }}
      >
        <BackButton
          component={Link}
          to={`/quest_editor/quest/${quest.id}`}
          sx={{ position: "absolute", left: 0 }}
        >
          {t("quest_edit_link.button.back")}
        </BackButton>
        <Stack direction="row" spacing={2}>
          <ActionButton
            size="small"
            onClick={handleSaveAndBack}
            disabled={isSubmitting}
          >
            {t("quest_edit_link.button.save_and_back")}
          </ActionButton>
          <ActionButton
            size="small"
            onClick={handleSaveAndNext}
            disabled={isSubmitting}
          >
            {t("quest_edit_link.button.save_and_goto_next")}
          </ActionButton>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default QuestEditLink;
